import React, { useState, useEffect, useRef, Component } from 'react';
// import moment from 'moment';
import moment from 'moment-timezone';

import WebSocketInstance from '../../services/WebSocket';
import { getRandomChatAvatarColor, stringForAvatar } from "../../utils/utils";
import { Avatar, Badge, IconButton } from "@mui/material";
import ChatIcon from '@mui/icons-material/Chat';
import { styled } from '@mui/material/styles';
import cookie from "react-cookies";

class ErrorBoundary extends Component {
    state = { hasError: false, error: null };
    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }
    render() {
        if (this.state.hasError) {
            console.error('ErrorBoundary caught:', this.state.error);
            return (
                <div style={{ padding: '10px', color: 'red' }}>
                    Error rendering messages: {this.state.error?.message || 'Unknown error'}. Please refresh.
                </div>
            );
        }
        return this.props.children;
    }
}

const StyledBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
        right: -3,
        top: -3,
        backgroundColor: '#ec053a',
        color: '#ffffff',
        fontSize: '12px',
        fontWeight: 900,
        padding: '4px 8px',
        borderRadius: '12px',
        minWidth: '20px',
        height: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
}));

const ChatWidget = ({ open = false, user_id, context, ip_address, username }) => {
    // console.log('ChatWidget props:', { open, user_id, context, ip_address, username });
    const [messages, setMessages] = useState([]);
    const [previousMessagesLength, setPreviousMessagesLength] = useState(0);
    const [newMessage, setNewMessage] = useState('');
    const [isOpen, setIsOpen] = useState(open);
    const [unreadCount, setUnreadCount] = useState(0);
    const [typingUsers, setTypingUsers] = useState([]);
    const [error, setError] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [connectionStatus, setConnectionStatus] = useState('disconnected');
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    // New state for dragging
    const [position, setPosition] = useState(() => {
        const saved = cookie.load('chatWidgetPosition')
        if (saved &&
    typeof saved.xPerc === 'number' &&
    typeof saved.yPerc === 'number'
  ) {
    return saved
  }
  return {
    xPerc: ((window.innerWidth - 85) / window.innerWidth) * 100,
    yPerc: 40,
  }
})
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState(null);
    const dragRef = useRef(null);
    const dragMovedRef = useRef(false);

    const typingTimeoutRef = useRef(null);
    const messageEndRef = useRef(null);
    const messageListRef = useRef(null);
    const scrollAnchorRef = useRef(null);
    const lastSeenMessageRef = useRef(null);

    const markAsSeen = (message) => {
  if (!message.id || lastSeenMessageRef.current === message.id) return;
  lastSeenMessageRef.current = message.id;
  WebSocketInstance.updateUnreadCount({ user_id, message_id: message.id });
  setUnreadCount(0);
};

    const mapMessageToUI = (msg) => ({
        position: msg.user_id === user_id ? 'right' : 'left',
        text: msg.content,
        title: msg.username || 'Anonymous',
        date: moment(msg.created_at).toDate(),
        id: msg.id,
    });

    const getTopmostVisibleMessage = () => {
        if (!messageListRef.current) return null;
        const messageElements = messageListRef.current.querySelectorAll('.message-item');
        let closestElement = null;
        let minDistance = Infinity;
        const listRect = messageListRef.current.getBoundingClientRect();

        for (const element of messageElements) {
            const rect = element.getBoundingClientRect();
            const distance = Math.abs(rect.top - listRect.top);
            if (distance < minDistance) {
                minDistance = distance;
                closestElement = element;
            }
        }

        if (closestElement) {
            const id = closestElement.getAttribute('data-message-id');
            const offset = closestElement.getBoundingClientRect().top - listRect.top;
            return { id, offset };
        }
        return null;
    };

const MIN_MARGIN=0
const handleMouseDown = (e) => {
  if (isOpen) return;
  e.preventDefault();
  dragMovedRef.current = false;         // reset on each new drag
  const widget = dragRef.current;
  if (!widget) return;
  const { left: widgetX, top: widgetY } = widget.getBoundingClientRect();
  setDragStart({ mouseX: e.clientX, mouseY: e.clientY, widgetX, widgetY });
  setIsDragging(true);
};

const handleMouseMove = (e) => {
  if (!isDragging || !dragStart) return;
  const { width, height } = dragRef.current.getBoundingClientRect();

  // mark that we’ve actually moved enough
  if (!dragMovedRef.current) {
    const dx = e.clientX - dragStart.mouseX;
    const dy = e.clientY - dragStart.mouseY;
    if (Math.hypot(dx, dy) > 5) {
      dragMovedRef.current = true;
    }
  }

  // compute new pos...
  const dx = e.clientX - dragStart.mouseX;
  const dy = e.clientY - dragStart.mouseY;
  let newX = dragStart.widgetX + dx;
  let newY = dragStart.widgetY + dy;

  // clamp
  newX = Math.max(0, Math.min(newX, window.innerWidth - width));
  newY = Math.max(0, Math.min(newY, window.innerHeight - height));

  // to vw/vh
  setPosition({
    xPerc: (newX / window.innerWidth) * 100,
    yPerc: (newY / window.innerHeight) * 100,
  });
};

const handleMouseUp = () => {
  if (isDragging) {
    setIsDragging(false);
    setDragStart(null);
  }
};

const handleClick = (e) => {
  // if we actually dragged, swallow this click
    e.stopPropagation();
  if (dragMovedRef.current) {
    dragMovedRef.current = false;
    return;
  }

  toggleChat();
};
    useEffect(() => {
  const handleResize = () => {
    const widget = dragRef.current;
    if (!widget) return;
    const { width, height } = widget.getBoundingClientRect();

    setPosition(({ xPerc, yPerc }) => {
      // percent → pixel
      const rawX = (xPerc / 100) * window.innerWidth;
      const rawY = (yPerc / 100) * window.innerHeight;
      // clamp to [MIN .. max-MIN]
      const clampedX = Math.min(
        Math.max(rawX, MIN_MARGIN),
        window.innerWidth  - width  - MIN_MARGIN
      );
      const clampedY = Math.min(
        Math.max(rawY, MIN_MARGIN),
        window.innerHeight - height - MIN_MARGIN
      );
      // back to %
      return {
        xPerc: (clampedX / window.innerWidth ) * 100,
        yPerc: (clampedY / window.innerHeight) * 100,
      };
    });
  };

  window.addEventListener('resize', handleResize);
  handleResize();          // ← run it right now on mount
  return () => window.removeEventListener('resize', handleResize);
}, []);

    // make sure you bind these to window during drag:
    useEffect(() => {
  if (isDragging) {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  } else {
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
  }
  return () => {
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
  };
}, [isDragging, dragStart]);


    useEffect(() => {
  // only save once drag has ended
  if (!isDragging) {
    cookie.save('chatWidgetPosition', position, {
      path: '/',              // make it site-wide
      maxAge: 60 * 60 * 24 * 365,  // 1 year
    })
  }
}, [position, isDragging])


    useEffect(() => {
        // console.log('ChatWidget context:', context, user_id, username, "Connection status:", connectionStatus);
        if (!user_id || !username) {
            setError('Please log in to chat');
            setConnectionStatus('disconnected');
            return;
        }

        WebSocketInstance.connect(context || null);

        const messagesCallback = ({ messages }) => {
            setPreviousMessagesLength(messages.length);
            setMessages((prev) => {
                const existingIds = new Set(prev.map((msg) => msg.id));
                const newMessages = messages
                    .filter((msg) => !existingIds.has(msg.id))
                    .reverse()
                    .map(mapMessageToUI);
                return [...newMessages, ...prev];
            });
            setHasMore(messages.length === 50);
            setIsLoadingMore(false);
        };

        const newMessageCallback = (message) => {
            setMessages((prev) => {
                if (prev.some((msg) => msg.id === message.id)) {
                    return prev;
                }
                const newMsg = mapMessageToUI(message);
                return [...prev, newMsg];
            });
                WebSocketInstance.fetchUnreadCount({ user_id });
        };

        const unreadCountCallback = (data) => {
            if (data.error || data.user_id !== String(user_id)) return;
            setUnreadCount(data.unread_count);
        };

       const errorCallback = ({ message }) => {
  console.error('WebSocket error:', message);
  setError(message);

};

        const initChatCallback = (data) => {
            console.log('Init chat response:');
            if (data.error) {
                setError(data.error);
            } else {
                setError(null);
                WebSocketInstance.fetchMessages({ user_id, before_timestamp: null });
                WebSocketInstance.fetchUnreadCount({ user_id });


            }
        };

        const connectionStatusCallback = ({ status }) => {
            setConnectionStatus(status);
            if (status === 'connected') {
                console.log('Connected to WebSocket: now fetching unread count');
                WebSocketInstance.fetchUnreadCount({ user_id });
            }
        };

        WebSocketInstance.addCallbacks({
            messagesCallback,
            newMessageCallback,
            unreadCountCallback,
            errorCallback,
            initChatCallback,
            connectionStatusCallback,
        });

        WebSocketInstance.waitForSocketConnection(() => {
            WebSocketInstance.initChatUser(username);
        });

        return () => {
            WebSocketInstance.removeCallbacks({
                messagesCallback,
                newMessageCallback,
                unreadCountCallback,
                errorCallback,
                initChatCallback,
                connectionStatusCallback,
            });
            WebSocketInstance.disconnect();
        };
    }, [user_id, username]);

    useEffect(() => {
        if (scrollAnchorRef.current && messageListRef.current && !isLoadingMore) {
            const { id, offset } = scrollAnchorRef.current;
            const element = messageListRef.current.querySelector(`[data-message-id="${id}"]`);
            if (element) {
                const listRect = messageListRef.current.getBoundingClientRect();
                const elementRect = element.getBoundingClientRect();
                const scrollTopBefore = messageListRef.current.scrollTop;
                const newScrollTop = elementRect.top - listRect.top + scrollTopBefore - offset;
                messageListRef.current.scrollTop = newScrollTop;
            } else {
                console.warn('Anchor message not found:', id);
                const currentScrollTop = messageListRef.current.scrollTop;
                messageListRef.current.scrollTop = currentScrollTop;
            }
            scrollAnchorRef.current = null;
        }
    }, [messages, isLoadingMore]);

    useEffect(() => {
        if (messageEndRef.current && isOpen && !scrollAnchorRef.current && messages.length > 0) {
            messageEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, isOpen]);
    // 2) separate effect to mark seen once everything is in sync
    useEffect(() => {
            // console.log('Checking for last seen message update:', { isOpen, unreadCount, messages });
         if (isOpen && messages.length > 0) {
         const latest = messages[messages.length - 1];
          markAsSeen(latest);
  }
                WebSocketInstance.fetchUnreadCount({ user_id });
        }, [ isOpen, messages]);

    const sendMessage = () => {
        if (newMessage.trim() && user_id) {
            // console.log('Sending message:', { user_id, content: newMessage, context, ip_address });
            WebSocketInstance.newChatMessage({
                user_id,
                content: newMessage,
                context,
                ip_address,
            });
            setNewMessage('');
            sendTyping(false);
        }
    };

    const sendTyping = (isTyping) => {
        if (isTyping) {
            setTypingUsers((prev) => [...new Set([...prev, username])]);
        } else {
            setTypingUsers((prev) => prev.filter((u) => u !== username));
        }
    };

    const handleTyping = (e) => {
        setNewMessage(e.target.value);
        sendTyping(true);
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => sendTyping(false), 1000);
    };

    const loadMoreMessages = () => {
        if (messages.length > 0 && hasMore && !isLoadingMore) {
            setIsLoadingMore(true);
            scrollAnchorRef.current = getTopmostVisibleMessage();
            const oldestMessage = messages[0];
            const beforeTimestamp = moment(oldestMessage.date).format('YYYY-MM-DDTHH:mm:ss.SSSZ');
            WebSocketInstance.fetchMessages({
                user_id,
                before_timestamp: beforeTimestamp,
            });
        }
    };

    const toggleChat = () => {
        setIsOpen((prev) => {
            const newIsOpen = !prev;
            return newIsOpen;
        });
    };

    // console.log('ChatWidget rendered:', { connectionStatus, messages, isOpen, unreadCount, typingUsers, error, position });

    return (
        <div
            className="chat-widget"
            ref={dragRef}
            style={{
                position: 'fixed',
                left:  isOpen ? 'auto' : `${position.xPerc}vw`,   // 1vw = 1% of viewport width
                top:   isOpen ? 'auto' : `${position.yPerc}vh`,
                bottom: isOpen ? '0px' : 'auto',
                right: isOpen ? '24px' : 'auto',
                width: isOpen ? '360px' : '60px',
                height: isOpen ? 'auto' : '60px',
                maxHeight: isOpen ? '520px' : '60px',
                background: isOpen ? '#ffffff' : 'transparent',
                borderRadius: isOpen ? '16px' : '50%',
                boxShadow: isOpen ? '0 8px 24px rgba(0, 0, 0, 0.2)' : '0 4px 12px rgba(0, 0, 0, 0.15)',
                zIndex: 20000,
                transition: isDragging ? 'none' : 'all 0.3s ease', // Disable transition during drag
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                transform: isOpen ? 'translateY(0)' : 'translateY(0)',
                opacity: 1,
                cursor: isOpen ? 'default' : 'grab', // Indicate draggable when closed
            }}
        >
            {!isOpen ? (
                <IconButton
                    onClick={handleClick}
                    onMouseDown={handleMouseDown}
                    sx={{
                        width: '60px',
                        height: '60px',
                        background: '#3A2244',
                        color: '#ffffff',
                        '&:hover': {
                            background: '#65347a',
                            transform: 'scale(1.05)',
                        },
                        transition: 'all 0.2s ease',
                        cursor: isDragging ? 'grabbing' : 'pointer',
                    }}
                >
                    <StyledBadge badgeContent={unreadCount} invisible={unreadCount === 0}>
                        <ChatIcon sx={{ fontSize: 28 }} />
                    </StyledBadge>
                </IconButton>
            ) : (
                <>
                    <div
                        className="chat-header"
                        onClick={toggleChat}
                        style={{
                            background: '#3A2244',
                            color: '#ffffff',
                            padding: '14px 20px',
                            borderRadius: '16px 16px 0 0',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontWeight: 700,
                            fontSize: '18px',
                            lineHeight: 1.2,
                        }}
                    >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            Chat
                            <span
                                style={{
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    backgroundColor:
                                        connectionStatus === 'connected'
                                            ? 'green'
                                            : connectionStatus === 'connecting'
                                            ? 'yellow'
                                            : 'red',
                                    animation: 'pulse 1s infinite',
                                }}
                            />
                        </span>
                        {unreadCount > 0 && (
                            <span
                                className="unread-count"
                                style={{
                                    background: '#65347a',
                                    color: '#ffffff',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    borderRadius: '12px',
                                    padding: '4px 10px',
                                    marginRight: '10px',
                                    lineHeight: 1,
                                    marginLeft: '10px',
                                }}
                            >
                                {unreadCount} unread
                            </span>
                        )}
                        <span>▼</span>
                    </div>
                    <div
                        className="chat-body"
                        style={{
                            maxHeight: '440px',
                            display: 'flex',
                            flexDirection: 'column',
                            overflowY: 'auto',
                            visibility: 'visible',
                        }}
                    >
                        <div
                            className="message-list"
                            ref={messageListRef}
                            style={{
                                flex: 1,
                                overflowY: 'auto',
                                padding: '20px',
                                scrollbarWidth: 'thin',
                                scrollbarColor: '#d1d5db #f9fafb',
                            }}
                        >
                            <ErrorBoundary>
                                {error && (
                                    <div style={{ padding: '10px', color: 'red' }}>
                                        {error}
                                        <button
                                            onClick={() => {
                                                setError(null);
                                                WebSocketInstance.disconnect();
                                                WebSocketInstance.connect(context || null);
                                                WebSocketInstance.waitForSocketConnection(() => {
                                                    WebSocketInstance.initChatUser(username);
                                                });
                                            }}
                                            style={{
                                                marginLeft: '10px',
                                                padding: '5px 10px',
                                                background: '#65347a',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: '12px',
                                            }}
                                        >
                                            Retry
                                        </button>
                                    </div>
                                )}
                                {hasMore && messages.length > 0 && (
                                    <button
                                        onClick={loadMoreMessages}
                                        disabled={!hasMore || isLoadingMore}
                                        style={{
                                            display: 'block',
                                            margin: '0 auto 10px',
                                            padding: '8px 16px',
                                            background: hasMore && !isLoadingMore ? '#65347a' : '#d1d5db',
                                            color: hasMore && !isLoadingMore ? '#ffffff' : '#6b728a',
                                            borderRadius: '12px',
                                            border: 'none',
                                            cursor: hasMore && !isLoadingMore ? 'pointer' : 'not-allowed',
                                            opacity: hasMore && !isLoadingMore ? 1 : 0.6,
                                        }}
                                    >
                                        {isLoadingMore ? 'Loading...' : 'Load More'}
                                    </button>
                                )}
                                {messages.length === 0 ? (
                                    <div style={{ padding: '10px', color: '#4b5563' }}>
                                        No messages yet.
                                    </div>
                                ) : (
                                    messages.map((msg) => (
                                        <div
                                            key={msg.id}
                                            className="message-item"
                                            data-message-id={msg.id}
                                            style={{
                                                display: 'flex',
                                                flexDirection: msg.position === 'left' ? 'row' : 'row-reverse',
                                                alignItems: 'flex-end',
                                                marginBottom: '12px',
                                            }}
                                            // onClick={() => {
                                            //     if (msg.position === 'left' && isOpen) {
                                            //         updateLastSeenMessage(msg.id);
                                            //     }
                                            // }}
                                        >
                                            <Avatar
                                                {...stringForAvatar(msg.title, false)}
                                                style={{
                                                    backgroundColor: getRandomChatAvatarColor(),
                                                    verticalAlign: 'middle',
                                                    margin: '0 8px',
                                                }}
                                                size={36}
                                            />
                                            <div
                                                className={`rce-mbox ${msg.position === 'left' ? 'rce-mbox-left' : 'rce-mbox-right'}`}
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    maxWidth: '70%',
                                                    alignItems: msg.position === 'left' ? 'flex-start' : 'flex-end',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        background: msg.position === 'left' ? '#c6a7d3' : '#bfdbfe',
                                                        color: '#111827',
                                                        padding: '4px 10px',
                                                        borderRadius: '12px',
                                                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                                        borderTopLeftRadius: msg.position === 'left' ? '4px' : '12px',
                                                        borderTopRightRadius: msg.position === 'right' ? '4px' : '12px',
                                                    }}
                                                >
                                                    {msg.position === 'left' && (
                                                        <div
                                                            style={{
                                                                fontWeight: 600,
                                                                fontSize: '11px',
                                                                marginBottom: '4px',
                                                                color: '#4f46e5',
                                                            }}
                                                        >
                                                            {msg.title}
                                                        </div>
                                                    )}
                                                    <div style={{ wordBreak: 'break-word' }}>{msg.text}</div>
                                                    <div
                                                        style={{
                                                            fontSize: '9px',
                                                            color: '#3d424a',
                                                            marginTop: '4px',
                                                            textAlign: 'right',
                                                            opacity: 0.85,
                                                        }}
                                                    >
                                                        {moment.tz(msg.date, moment.tz.guess(true)).format('MMM D, h:mm A')}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                                <div ref={messageEndRef} />
                            </ErrorBoundary>
                        </div>
                        {typingUsers.length > 0 && (
                            <div
                                className="typing-indicator"
                                style={{
                                    padding: '8px 16px',
                                    color: '#6b7280',
                                    fontStyle: 'italic',
                                    fontSize: '14px',
                                    background: '#f9fafb',
                                    borderTop: '1px solid #e5e7eb',
                                }}
                            >
                                {/* {typingUsers.join(', ')} {typingUsers.length > 1 ? 'are' : 'is'} typing */}
                            </div>
                        )}
                        <div
                            className="chat-input"
                            style={{
                                padding: '12px 16px',
                                borderTop: '1px solid #e5e7eb',
                                background: '#ffffff',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                                <textarea
                                    placeholder="Type a message..."
                                    value={newMessage}
                                    onChange={handleTyping}
                                    onInput={(e) => {
                                        e.target.style.height = 'auto';
                                        e.target.style.height = `${e.target.scrollHeight}px`;
                                    }}
                                    rows={1}
                                    className="chat-textarea"
                                    style={{
                                        flex: 1,
                                        border: '2px solid #3A2244',
                                        borderRadius: '12px',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        color: '#374151',
                                        resize: 'none',
                                        overflow: 'hidden',
                                        outline: 'none',
                                        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                                        minHeight: '40px',
                                        maxHeight: '120px',
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            sendMessage();
                                        }
                                    }}
                                />
                                <button
                                    onClick={sendMessage}
                                    className="rce-button"
                                    style={{
                                        background: '#65347a',
                                        color: '#ffffff',
                                        borderRadius: '12px',
                                        padding: '8px 16px',
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        transition: 'background-color 0.2s ease, transform 0.15s ease',
                                        flexShrink: 0,
                                    }}
                                >
                                    Send
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
            <style>
                {`
                    .chat-body::-webkit-scrollbar,
                    .message-list::-webkit-scrollbar {
                        width: 6px;
                    }
                    .chat-body::-webkit-scrollbar-track,
                    .message-list::-webkit-scrollbar-track {
                        background: #f9fafb;
                        borderRadius: 3px;
                    }
                    .chat-body::-webkit-scrollbar-thumb,
                    .message-list::-webkit-scrollbar-thumb {
                        background: #d1d5db;
                        borderRadius: 3px;
                    }
                    .chat-textarea:focus {
                        border-color: #4f46e5;
                        box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
                    }
                    .rce-button:hover {
                        background: #3a2250 !important;
                        transform: translateY(-1px);
                    }
                    .rce-button:active {
                        transform: translateY(0);
                    }
                    @media (max-width: 480px) {
                        .chat-widget {
                            width: ${isOpen ? '100%' : '60px'};
                            max-height: ${isOpen ? '90vh' : '60px'};
                            bottom: ${isOpen ? '12px' : 'auto'};
                            right: ${isOpen ? '12px' : 'auto'};
                            border-radius: ${isOpen ? '12px' : '50%'};
                        }
                        .chat-body {
                            max-height: 70vh;
                        }
                        .rce-mbox {
                            max-width: 80% !important;
                        }
                    }
                    @keyframes blink {
                        0% { opacity: 0.3; }
                        20% { opacity: 1; }
                        100% { opacity: 0.3; }
                    }
                    @keyframes pulse {
                        0% { opacity: 0.6; }
                        50% { opacity: 1; }
                        100% { opacity: 0.6; }
                    }
                `}
            </style>
        </div>
    );
};

export default ChatWidget;